// src/pages/admin/ScheduleWindows.jsx — Gestão de Janelas de Agenda
import React, { useState, useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useApp } from 'contexts/AppContext';
import { scheduleWindowsService, notificationsService } from 'infrastructure/firebase';
import { Button, Avatar, Alert, FilterChip, EmptyState } from 'shared/components/ui';
import { formatDate } from 'shared/utils/helpers';
import { useMobile } from 'shared/hooks/useMobile';
import { validateWindowConflicts, slotsInRange } from 'shared/utils/scheduleLogic';

const T = {
  brand:'#6B5876', border:'#E8E5EC', s2:'#FAF7FB', s3:'#F0EDF3',
  text:'#3D3447', text2:'#6B6070', muted:'#A8A1B0',
  success:'#8FC2A6', successBg:'#E7F4ED', successFg:'#2D5F43',
  warning:'#DDB48E', warningBg:'#F5E4D2', warningFg:'#80551F',
  danger:'#C9929A', dangerBg:'#F5E6E8', dangerFg:'#7A3C44',
  sans:"'DM Sans',sans-serif", serif:"'Instrument Serif',serif",
};

const TIME_OPTIONS = slotsInRange('07:00', '20:00').concat(['20:00']);

const STATUS_INFO = {
  pending:  { label:'Aguardando', color:T.warningFg,  bg:T.warningBg,  icon:'⏳' },
  approved: { label:'Aprovado',   color:T.successFg,  bg:T.successBg,  icon:'✓' },
  rejected: { label:'Recusado',   color:T.dangerFg,   bg:T.dangerBg,   icon:'✗' },
};

// ── Form Panel ──────────────────────────────────────────────────
function WindowFormPanel({ win, professionals, appointments, windows, onClose, showToast, adminId }) {
  const isMobile = useMobile();
  const isNew = !win?.id;
  const [form, setForm] = useState({
    allProfessionals: win?.allProfessionals ?? false,
    professionalId:   win?.professionalId  ?? '',
    date:             win?.date            ?? '',
    startTime:        win?.startTime       ?? '08:00',
    endTime:          win?.endTime         ?? '12:00',
    reason:           win?.reason          ?? '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pro = professionals.find(p => p.id === form.professionalId);

  const conflicts = useMemo(() => {
    if (!form.date || !form.startTime || !form.endTime) return [];
    return validateWindowConflicts({
      date: form.date,
      startTime: form.startTime,
      endTime: form.endTime,
      professionalId: form.professionalId,
      allProfessionals: form.allProfessionals,
      appointments,
      windows,
      excludeWindowId: win?.id,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date, form.startTime, form.endTime, form.professionalId, form.allProfessionals, appointments, windows, win?.id]);

  const canSave = form.date && form.startTime && form.endTime
    && (form.allProfessionals || form.professionalId)
    && form.startTime < form.endTime;

  const handleSave = async () => {
    setSaving(true);
    try {
      const proObj = professionals.find(p => p.id === form.professionalId);
      const data = {
        professionalId:   form.allProfessionals ? null : form.professionalId,
        professionalName: form.allProfessionals ? null : proObj?.name,
        allProfessionals: form.allProfessionals,
        date:       form.date,
        startTime:  form.startTime,
        endTime:    form.endTime,
        reason:     form.reason,
        status:     'approved', // admin → aprovado imediatamente
        createdBy:  adminId,
        createdByRole: 'admin',
        approvedBy: adminId,
        approvedAt: new Date(),
      };
      if (isNew) {
        await scheduleWindowsService.add(data);
        showToast('Janela criada e aprovada!');
      } else {
        await scheduleWindowsService.update(win.id, data);
        showToast('Janela atualizada!');
      }
      onClose();
    } catch {
      showToast('Erro ao salvar janela.', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir esta janela?')) return;
    try {
      await scheduleWindowsService.delete(win.id);
      showToast('Janela removida.');
      onClose();
    } catch { showToast('Erro ao excluir janela.', 'danger'); }
  };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(61,52,71,0.32)', backdropFilter:'blur(6px)', zIndex:150 }} />
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:'min(480px, 100vw)', background:'#fff', zIndex:151, boxShadow:'-12px 0 40px rgba(74,63,88,0.18)', display:'flex', flexDirection:'column', fontFamily:T.sans }}>
        {/* Header */}
        <div style={{ padding:'22px 28px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontFamily:T.serif, fontSize:22, color:T.text }}>{isNew ? 'Nova janela de bloqueio' : 'Editar janela'}</div>
            <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>Período em que o(s) profissional(is) estão indisponíveis</div>
          </div>
          <button onClick={onClose} style={{ background:T.s3, border:'none', cursor:'pointer', width:32, height:32, borderRadius:8, color:T.text2, fontSize:18 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding:'24px 28px', flex:1, overflowY:'auto', display:'flex', flexDirection:'column', gap:18 }}>

          {/* Profissional */}
          <div>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:T.text2, marginBottom:8 }}>Quem será bloqueado</label>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', border:`1.5px solid ${form.allProfessionals ? T.brand : T.border}`, borderRadius:12, cursor:'pointer', background: form.allProfessionals ? T.s2 : '#fff' }}>
                <input type="radio" checked={form.allProfessionals} onChange={() => { set('allProfessionals', true); set('professionalId', ''); }} style={{ accentColor:T.brand }} />
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:T.text }}>Todos os profissionais</div>
                  <div style={{ fontSize:11, color:T.muted }}>Reunião de equipe, feriado, etc.</div>
                </div>
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', border:`1.5px solid ${!form.allProfessionals ? T.brand : T.border}`, borderRadius:12, cursor:'pointer', background: !form.allProfessionals ? T.s2 : '#fff' }}>
                <input type="radio" checked={!form.allProfessionals} onChange={() => set('allProfessionals', false)} style={{ accentColor:T.brand }} />
                <div style={{ fontWeight:600, fontSize:13, color:T.text }}>Profissional específico</div>
              </label>
            </div>
            {!form.allProfessionals && (
              <div style={{ marginTop:10 }}>
                <select value={form.professionalId} onChange={e => set('professionalId', e.target.value)}
                  style={{ width:'100%', padding:'10px 14px', border:`1.5px solid ${T.border}`, borderRadius:12, fontSize:14, outline:'none', color:T.text, background:'#fff' }}>
                  <option value="">Selecione um profissional</option>
                  {professionals.map(p => <option key={p.id} value={p.id}>{p.name} — {p.specialty}</option>)}
                </select>
                {pro && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:8, padding:'8px 12px', background:T.s2, borderRadius:10 }}>
                    <Avatar name={pro.name} color={pro.avatarColor} size="sm" />
                    <span style={{ fontSize:13, color:T.text }}>{pro.name}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Data */}
          <div>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:T.text2, marginBottom:6 }}>Data <span style={{ color:T.danger }}>*</span></label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)} min={new Date().toISOString().split('T')[0]}
              style={{ width:'100%', padding:'10px 14px', border:`1.5px solid ${T.border}`, borderRadius:12, fontSize:14, outline:'none', color:T.text, background:'#fff', boxSizing:'border-box' }} />
          </div>

          {/* Horários */}
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:14 }}>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:500, color:T.text2, marginBottom:6 }}>Início <span style={{ color:T.danger }}>*</span></label>
              <select value={form.startTime} onChange={e => set('startTime', e.target.value)}
                style={{ width:'100%', padding:'10px 14px', border:`1.5px solid ${T.border}`, borderRadius:12, fontSize:14, outline:'none', color:T.text, background:'#fff' }}>
                {TIME_OPTIONS.slice(0, -1).map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:500, color:T.text2, marginBottom:6 }}>Fim <span style={{ color:T.danger }}>*</span></label>
              <select value={form.endTime} onChange={e => set('endTime', e.target.value)}
                style={{ width:'100%', padding:'10px 14px', border:`1.5px solid ${T.border}`, borderRadius:12, fontSize:14, outline:'none', color:T.text, background:'#fff' }}>
                {TIME_OPTIONS.slice(1).map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          {form.startTime >= form.endTime && (
            <div style={{ fontSize:12, color:T.dangerFg, padding:'8px 12px', background:T.dangerBg, borderRadius:8 }}>
              ⚠️ O horário de fim deve ser após o início.
            </div>
          )}

          {/* Motivo */}
          <div>
            <label style={{ display:'block', fontSize:13, fontWeight:500, color:T.text2, marginBottom:6 }}>Motivo</label>
            <input value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="Ex.: Almoço, Reunião, Férias..."
              style={{ width:'100%', padding:'10px 14px', border:`1.5px solid ${T.border}`, borderRadius:12, fontSize:14, outline:'none', color:T.text, background:'#fff', boxSizing:'border-box' }} />
          </div>

          {/* Conflitos */}
          {conflicts.length > 0 && (
            <div style={{ background:T.warningBg, border:`1px solid ${T.warning}`, borderRadius:12, padding:'12px 14px' }}>
              <div style={{ fontWeight:600, fontSize:13, color:T.warningFg, marginBottom:6 }}>⚠️ Atenção — {conflicts.length} conflito(s)</div>
              {conflicts.map((c, i) => (
                <div key={i} style={{ fontSize:12, color:T.warningFg, marginTop:4 }}>• {c.message}</div>
              ))}
              <div style={{ fontSize:11, color:T.warningFg, marginTop:8, opacity:.8 }}>Você pode criar mesmo assim. Agendamentos existentes não serão cancelados automaticamente.</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'16px 28px', borderTop:`1px solid ${T.border}`, background:T.s2, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          {!isNew
            ? <Button variant="ghost" size="sm" onClick={handleDelete} style={{ color:T.dangerFg }}>🗑 Excluir</Button>
            : <span />}
          <div style={{ display:'flex', gap:10 }}>
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button variant="primary" disabled={!canSave || saving} loading={saving} onClick={handleSave}>
              {isNew ? 'Criar janela' : 'Salvar'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Approval Panel for pending requests ────────────────────────
function ApprovalCard({ win, professionals, onApprove, onReject }) {
  const [loading, setLoading] = useState(false);
  const pro = professionals.find(p => p.id === win.professionalId);

  const handle = async (fn) => { setLoading(true); await fn(); setLoading(false); };

  return (
    <div style={{ border:`1.5px solid ${T.warning}`, borderRadius:14, background:T.warningBg, padding:'16px 18px', display:'flex', gap:14, alignItems:'flex-start' }}>
      <div style={{ fontSize:24, flexShrink:0 }}>⏳</div>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:600, fontSize:14, color:T.warningFg, marginBottom:4 }}>
          {pro ? pro.name : 'Profissional'} solicitou bloqueio de agenda
        </div>
        <div style={{ fontSize:13, color:T.warningFg, marginBottom:6 }}>
          {formatDate(win.date)} · {win.startTime}–{win.endTime}
          {win.reason && <span style={{ marginLeft:6, fontStyle:'italic' }}>"{win.reason}"</span>}
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <Button variant="success" size="sm" loading={loading} onClick={() => handle(onApprove)}>✓ Aprovar</Button>
          <Button variant="danger"  size="sm" loading={loading} onClick={() => handle(onReject)}>✗ Recusar</Button>
        </div>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────
export default function AdminScheduleWindows() {
  const { userProfile } = useAuth();
  const { scheduleWindows, pendingWindows, professionals, appointments, showToast } = useApp();
  const [tab, setTab]         = useState('all');
  const [editing, setEditing] = useState(null);
  const [filterPro, setFilterPro] = useState('all');

  const filtered = scheduleWindows.filter(w => {
    if (tab === 'pending'  && w.status !== 'pending')  return false;
    if (tab === 'approved' && w.status !== 'approved') return false;
    if (tab === 'rejected' && w.status !== 'rejected') return false;
    if (filterPro !== 'all' && !w.allProfessionals && w.professionalId !== filterPro) return false;
    return true;
  }).sort((a, b) => a.date > b.date ? 1 : a.startTime > b.startTime ? 1 : -1);

  const handleApprove = async (win) => {
    try {
      const approvedBy = userProfile?.id || userProfile?.uid;
      await scheduleWindowsService.approve(win.id, approvedBy);
      if (win.professionalId) {
        await notificationsService.add({
          userId: win.professionalId,
          type: 'window_approved',
          title: 'Janela de agenda aprovada',
          message: `Sua solicitação de bloqueio para ${formatDate(win.date)} (${win.startTime}–${win.endTime}) foi aprovada.`,
        });
      }
      showToast('Janela aprovada! Os slots estão bloqueados.');
    } catch { showToast('Erro ao aprovar janela.', 'danger'); }
  };

  const handleReject = async (win) => {
    try {
      await scheduleWindowsService.reject(win.id);
      if (win.professionalId) {
        await notificationsService.add({
          userId: win.professionalId,
          type: 'window_rejected',
          title: 'Janela de agenda recusada',
          message: `Sua solicitação de bloqueio para ${formatDate(win.date)} (${win.startTime}–${win.endTime}) foi recusada pelo administrador.`,
        });
      }
      showToast('Janela recusada.');
    } catch { showToast('Erro ao recusar janela.', 'danger'); }
  };

  return (
    <div style={{ fontFamily:T.sans }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <div style={{ fontFamily:T.serif, fontSize:32, color:T.text }}>Janelas de Agenda</div>
          <div style={{ fontSize:13, color:T.muted, marginTop:2 }}>
            Bloqueios de horário por profissional · {scheduleWindows.length} janelas cadastradas
          </div>
        </div>
        <Button variant="primary" onClick={() => setEditing({})}>+ Nova janela</Button>
      </div>

      {/* Pending approvals */}
      {pendingWindows.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <Alert type="warning" title={`${pendingWindows.length} solicitação(ões) aguardando aprovação`}>
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginTop:10 }}>
              {pendingWindows.map(w => (
                <ApprovalCard key={w.id} win={w} professionals={professionals}
                  onApprove={() => handleApprove(w)}
                  onReject={() => handleReject(w)} />
              ))}
            </div>
          </Alert>
        </div>
      )}

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:20, alignItems:'center', flexWrap:'wrap' }}>
        <FilterChip label={`Todos (${scheduleWindows.length})`}        active={tab==='all'}      onClick={() => setTab('all')} />
        <FilterChip label={`Aprovados (${scheduleWindows.filter(w=>w.status==='approved').length})`} active={tab==='approved'} onClick={() => setTab('approved')} />
        <FilterChip label={`Pendentes (${pendingWindows.length})`}     active={tab==='pending'}  onClick={() => setTab('pending')} />
        <FilterChip label={`Recusados (${scheduleWindows.filter(w=>w.status==='rejected').length})`} active={tab==='rejected'} onClick={() => setTab('rejected')} />
        <div style={{ marginLeft:'auto' }}>
          <select value={filterPro} onChange={e => setFilterPro(e.target.value)}
            style={{ padding:'8px 14px', border:`1.5px solid ${T.border}`, borderRadius:10, fontSize:13, outline:'none', color:T.text, background:'#fff' }}>
            <option value="all">Todos os profissionais</option>
            {professionals.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState icon="🗓️" title="Nenhuma janela encontrada" text="Crie janelas de bloqueio para organizar a disponibilidade dos profissionais." />
      ) : (
        <div style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:16, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr>
                {['Profissional','Data','Horário','Motivo','Criado por','Status','Ações'].map((h, i) => (
                  <th key={i} style={{ textAlign:'left', padding:'10px 16px', fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:1, color:T.muted, borderBottom:`1px solid ${T.border}`, background:T.s2 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(w => {
                const pro = professionals.find(p => p.id === w.professionalId);
                const si  = STATUS_INFO[w.status] || STATUS_INFO.pending;
                return (
                  <tr key={w.id}>
                    <td style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}` }}>
                      {w.allProfessionals ? (
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:28, height:28, borderRadius:'50%', background:T.s3, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>👥</div>
                          <span style={{ fontWeight:500, color:T.text }}>Todos</span>
                        </div>
                      ) : pro ? (
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <Avatar name={pro.name} color={pro.avatarColor} size="sm" />
                          <span style={{ fontWeight:500, color:T.text }}>{pro.name}</span>
                        </div>
                      ) : (
                        <span style={{ color:T.muted }}>—</span>
                      )}
                    </td>
                    <td style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}`, color:T.text }}>{formatDate(w.date)}</td>
                    <td style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}`, fontWeight:500, color:T.text }}>{w.startTime} – {w.endTime}</td>
                    <td style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}`, color:T.text2 }}>{w.reason || <span style={{ color:T.muted, fontStyle:'italic' }}>—</span>}</td>
                    <td style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}`, color:T.muted, fontSize:12 }}>
                      {w.createdByRole === 'admin' ? '🛡️ Admin' : '👤 Profissional'}
                    </td>
                    <td style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}` }}>
                      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background:si.bg, color:si.color }}>
                        {si.icon} {si.label}
                      </span>
                    </td>
                    <td style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}` }}>
                      <div style={{ display:'flex', gap:6 }}>
                        <Button variant="secondary" size="sm" onClick={() => setEditing(w)}>Editar</Button>
                        {w.status === 'pending' && (
                          <>
                            <Button variant="success" size="sm" onClick={() => handleApprove(w)}>✓</Button>
                            <Button variant="danger"  size="sm" onClick={() => handleReject(w)}>✗</Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Side panel */}
      {editing !== null && (
        <WindowFormPanel
          win={editing}
          professionals={professionals}
          appointments={appointments}
          windows={scheduleWindows}
          adminId={userProfile?.id}
          showToast={showToast}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
